/**
 * Small FIFO semaphore for browser requests.
 *
 * It is intentionally transport-agnostic. fetchWithTimeout uses the shared
 * backendApiRequestGate for /api/* requests so one PWA tab cannot consume an
 * unbounded number of backend DB sessions during polling/fan-out bursts.
 */
export function createRequestGate({ maxConcurrent = 4 } = {}) {
  const limit = Math.max(1, Math.floor(Number(maxConcurrent) || 1));
  let active = 0;
  const queue = [];

  const abortError = () => new DOMException("Request abgebrochen.", "AbortError");

  const pump = () => {
    while (active < limit && queue.length > 0) {
      const item = queue.shift();
      if (!item || item.cancelled) continue;
      if (item.signal?.aborted) {
        item.cancelled = true;
        item.cleanup?.();
        item.reject(abortError());
        continue;
      }
      item.cleanup?.();
      active += 1;
      let released = false;
      item.resolve(() => {
        if (released) return;
        released = true;
        active = Math.max(0, active - 1);
        pump();
      });
    }
  };

  const acquire = (signal) => new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError());
      return;
    }

    const item = { signal, resolve, reject, cancelled: false, cleanup: null };
    if (signal) {
      const onAbort = () => {
        if (item.cancelled) return;
        item.cancelled = true;
        item.cleanup?.();
        reject(abortError());
        pump();
      };
      signal.addEventListener("abort", onAbort, { once: true });
      item.cleanup = () => signal.removeEventListener("abort", onAbort);
    }
    queue.push(item);
    pump();
  });

  return {
    acquire,
    getState: () => ({ active, queued: queue.filter((item) => !item.cancelled).length, maxConcurrent: limit }),
  };
}

export const backendApiRequestGate = createRequestGate({ maxConcurrent: 4 });

export function isBackendApiRequest(input) {
  if (typeof input === "string") {
    if (input.startsWith("/api/")) return true;
    try {
      const base = globalThis.location?.origin ?? "http://localhost";
      return new URL(input, base).pathname.startsWith("/api/");
    } catch {
      return false;
    }
  }
  if (input instanceof URL) return input.pathname.startsWith("/api/");
  if (typeof Request !== "undefined" && input instanceof Request) {
    try { return new URL(input.url).pathname.startsWith("/api/"); } catch { return false; }
  }
  return false;
}
